import React, { useState } from 'react';
import {
  UkraineFlag,

  USFlag,
  FranceFlag,
  GermanyFlag,
  ItalyFlag,
  SpainFlag,
  NetherlandsFlag,
  SwitzerlandFlag,
  AustriaFlag,
  BelgiumFlag,
  PolandFlag,
  CzechFlag,
  HungaryFlag,
  RomaniaFlag,
  DenmarkFlag,
  SwedenFlag,
  NorwayFlag,
  FinlandFlag,
  GreeceFlag
} from '../Flags';
import './style.css';

interface PhoneInputProps {
  // value?: string; // Not used, value is combined from countryCode + localNumber
  id?: string;
  countryCode?: string;
  localNumber?: string;
  onChange?: (phone: string, countryCode: string, localNumber: string) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}

// Популярні коди країн
const COUNTRY_CODES = [
  { code: '+380', flag: <UkraineFlag />, country: 'Україна', label: '+380' },
  { code: '+1', flag: <USFlag />, country: 'США/Канада', label: '+1' },

  { code: '+33', flag: <FranceFlag />, country: 'Франція', label: '+33' },
  { code: '+49', flag: <GermanyFlag />, country: 'Німеччина', label: '+49' },
  { code: '+39', flag: <ItalyFlag />, country: 'Італія', label: '+39' },
  { code: '+34', flag: <SpainFlag />, country: 'Іспанія', label: '+34' },
  { code: '+31', flag: <NetherlandsFlag />, country: 'Нідерланди', label: '+31' },
  { code: '+41', flag: <SwitzerlandFlag />, country: 'Швейцарія', label: '+41' },
  { code: '+43', flag: <AustriaFlag />, country: 'Австрія', label: '+43' },
  { code: '+32', flag: <BelgiumFlag />, country: 'Бельгія', label: '+32' },
  { code: '+48', flag: <PolandFlag />, country: 'Польща', label: '+48' },
  { code: '+420', flag: <CzechFlag />, country: 'Чехія', label: '+420' },
  { code: '+36', flag: <HungaryFlag />, country: 'Угорщина', label: '+36' },
  { code: '+40', flag: <RomaniaFlag />, country: 'Румунія', label: '+40' },
  { code: '+45', flag: <DenmarkFlag />, country: 'Данія', label: '+45' },
  { code: '+46', flag: <SwedenFlag />, country: 'Швеція', label: '+46' },
  { code: '+47', flag: <NorwayFlag />, country: 'Норвегія', label: '+47' },
  { code: '+358', flag: <FinlandFlag />, country: 'Фінляндія', label: '+358' },
  { code: '+30', flag: <GreeceFlag />, country: 'Греція', label: '+30' },
];

/**
 * PhoneInput компонент для введення телефону з розділенням коду країни
 * @component
 * @example
 * const [phone, setPhone] = useState('');
 * const [countryCode, setCountryCode] = useState('+380');
 * const [localNumber, setLocalNumber] = useState('');
 * 
 * return (
 *   <PhoneInput
 *     countryCode={countryCode}
 *     localNumber={localNumber}
 *     onChange={(phone, code, local) => {
 *       setPhone(phone);
 *       setCountryCode(code);
 *       setLocalNumber(local);
 *     }}
 *   />
 * )
 */
const PhoneInput: React.FC<PhoneInputProps> = ({
  id,
  countryCode = '+380',
  localNumber = '',
  onChange,
  placeholder = 'Введіть номер телефону',
  disabled = false,
  required = false,
}) => {
  const [selectedCode, setSelectedCode] = useState(countryCode);
  const [localNum, setLocalNum] = useState(localNumber);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Отримати прапор для вибраного коду
  const getSelectedFlag = (): React.ReactNode => {
    const item = COUNTRY_CODES.find(c => c.code === selectedCode);
    return item?.flag || '';
  };

  // Форматувати номер з пробілами для відображення (683781977 -> 68 378 19 77)
  const formatPhoneDisplay = (digits: string): string => {
    if (!digits) return '';
    // Розділяємо на групи: 2-3-2-2
    const match = digits.match(/^(\d{1,2})(\d{0,3})(\d{0,2})(\d{0,2})$/);
    if (match) {
      return [match[1], match[2], match[3], match[4]]
        .filter(group => group)
        .join(' ');
    }
    return digits;
  };

  const handleCodeChange = (code: string) => {
    setSelectedCode(code);
    setIsDropdownOpen(false);
    
    if (onChange) {
      onChange(`${code}${localNum}`, code, localNum);
    }
  };

  const handleLocalNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Видаляємо всі символи крім цифр та +
    const digits = e.target.value.replace(/[^\d+]/g, '');
    
    setLocalNum(digits);
    
    if (onChange) {
      onChange(`${selectedCode}${digits}`, selectedCode, digits);
    }
  };

  const handleLocalNumberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Дозволяємо цифри, Backspace, Delete, Tab, Escape, Enter, Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight'];
    
    if (!allowedKeys.includes(e.key) && !e.ctrlKey && !e.metaKey) {
      e.preventDefault();
    }
  };

  return (
    <div className="phone-input-wrapper">
      <div className="phone-input-container">
        {/* Country Code Selector */}
        <div className="country-code-selector">
          <button
            type="button"
            className="country-code-button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            disabled={disabled}
          >
            <span className="country-flag">{getSelectedFlag()}</span>
            <span className="country-code-label">{selectedCode}</span>
            <i className={`bi bi-chevron-down ${isDropdownOpen ? 'open' : ''}`}></i>
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="country-code-dropdown">
              <input
                type="text"
                className="dropdown-search"
                placeholder="Пошук коду..."
                onChange={() => {
                  // Filter functionality can be added here if needed
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="dropdown-list">
                {COUNTRY_CODES.map((item) => (
                  <button
                    key={item.code}
                    type="button"
                    className={`dropdown-item ${selectedCode === item.code ? 'active' : ''}`}
                    onClick={() => handleCodeChange(item.code)}
                  >
                    <span className="country-flag">{item.flag}</span>
                    <span className="country-code">{item.code}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Local Phone Number Input */}
        <input
          type="tel"
          id={id}
          className="local-phone-input"
          value={formatPhoneDisplay(localNum)}
          onChange={handleLocalNumberChange}
          onKeyDown={handleLocalNumberKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
        />
      </div>

      {/* Full Phone Display */}
      {/* {localNum && (
        <div className="phone-preview">
          <small>Повний номер: {selectedCode}{formatPhoneDisplay(localNum)}</small>
        </div>
      )} */}
    </div>
  );
};

export default PhoneInput;
